import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Tooltip
} from '@mui/material';
import { 
  Search, 
  Add, 
  Edit, 
  Delete, 
  Visibility, 
  ContentCopy,
  Assignment,
  ToggleOn,
  ToggleOff,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useSafeBack } from '../hooks/useSafeBack';
import assessmentService, { Assessment, AssessmentStatistics } from '../services/assessmentService';
import { toast } from 'react-toastify';
import AssignSurveyDialog from '../components/AssignSurveyDialog';

const ManageSurveysPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // State
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    page: 0,
    size: 10,
    search: '',
    category: '',
    active: undefined as boolean | undefined
  });
  const [pagination, setPagination] = useState({
    totalElements: 0,
    totalPages: 0,
    currentPage: 0
  });
  const [selectedAssessment, setSelectedAssessment] = useState<Assessment | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [statistics, setStatistics] = useState<Map<number, AssessmentStatistics>>(new Map());

  // Load assessments
  const loadAssessments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await assessmentService.getAssessments(
        filters.page,
        filters.size,
        filters.search,
        filters.category,
        filters.active
      );
      setAssessments(response.content || []);
      setPagination({
        totalElements: response.totalElements || 0,
        totalPages: response.totalPages || 0,
        currentPage: response.page || 0
      });

      // Load statistics for each assessment
      const statsPromises = (response.content || []).map((assessment: Assessment) =>
        assessmentService.getAssessmentStatistics(assessment.id)
          .then(stats => ({ id: assessment.id, stats }))
          .catch(() => ({ id: assessment.id, stats: null }))
      );
      
      const statsResults = await Promise.all(statsPromises);
      const statsMap = new Map<number, AssessmentStatistics>();
      statsResults.forEach(result => {
        if (result.stats) {
          statsMap.set(result.id, result.stats);
        }
      });
      setStatistics(statsMap);
    } catch (err: any) {
      setError(err.message || 'Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  // Load categories
  const loadCategories = async () => {
    try {
      const categoriesData = await assessmentService.getAssessmentCategories();
      setCategories(categoriesData);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  useEffect(() => {
    loadAssessments();
    loadCategories();
  }, [filters]);

  // Event handlers
  const handleLogout = () => {
    logout();
  };

  const goBack = useSafeBack('/dashboard');

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({
      ...prev,
      search: event.target.value,
      page: 0
    }));
  };

  const handleCategoryChange = (event: any) => {
    setFilters(prev => ({
      ...prev,
      category: event.target.value,
      page: 0
    }));
  };

  const handleStatusChange = (event: any) => {
    setFilters(prev => ({
      ...prev,
      active: event.target.value === '' ? undefined : event.target.value === 'active',
      page: 0
    }));
  };

  const handlePageChange = (event: any, newPage: number) => {
    setFilters(prev => ({
      ...prev,
      page: newPage - 1
    }));
  };

  const handleCreateAssessment = () => {
    navigate('/create-assessment');
  };

  const handleEditAssessment = (assessment: Assessment) => {
    navigate(`/edit-survey/${assessment.id}`);
  };

  const handleDeleteAssessment = (assessment: Assessment) => {
    setSelectedAssessment(assessment);
    setShowDeleteDialog(true);
  };

  const confirmDeleteAssessment = async () => {
    if (!selectedAssessment) return;

    try {
      setLoading(true);
      await assessmentService.deleteAssessment(selectedAssessment.id);
      setShowDeleteDialog(false);
      setSelectedAssessment(null);
      toast.success('Assessment deleted successfully');
      loadAssessments();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete assessment');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (assessment: Assessment) => {
    try {
      setLoading(true);
      if (assessment.isActive) {
        await assessmentService.deactivateAssessment(assessment.id);
        toast.success('Assessment deactivated');
      } else {
        await assessmentService.activateAssessment(assessment.id);
        toast.success('Assessment activated');
      }
      loadAssessments();
    } catch (err: any) {
      toast.error(err.message || 'Failed to update assessment status');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignAssessment = (assessment: Assessment) => {
    setSelectedAssessment(assessment);
    setShowAssignDialog(true);
  };

  const handleDuplicateAssessment = async (assessment: Assessment) => {
    try {
      setLoading(true);
      const newTitle = `${assessment.title} (Copy)`;
      await assessmentService.duplicateAssessment(assessment.id, newTitle);
      toast.success('Assessment duplicated successfully');
      loadAssessments();
    } catch (err: any) {
      toast.error(err.message || 'Failed to duplicate assessment');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAssessment = (assessment: Assessment) => {
    navigate(`/assessments/${assessment.id}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Manage Surveys
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user?.firstName?.charAt(0) || 'U'}
            </Avatar>
            <Typography variant="body1">
              {user?.firstName} {user?.lastName}
            </Typography>
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Manage Surveys & Assessments
            </Typography>
            <Button onClick={goBack} sx={{ mb: 2 }}>
              ← Back to Dashboard
            </Button>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateAssessment}
          >
            Create New Survey
          </Button>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search surveys..."
                  value={filters.search}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={filters.category || ''}
                    label="Category"
                    onChange={handleCategoryChange}
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map(category => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.active === undefined ? '' : filters.active ? 'active' : 'inactive'}
                    label="Status"
                    onChange={handleStatusChange}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography variant="body2" color="textSecondary">
                  Total: {pagination.totalElements} surveys
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && <LinearProgress sx={{ mb: 3 }} />}

        {/* Assessments Table */}
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Survey Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Questions</TableCell>
                  <TableCell align="center">Assignments</TableCell>
                  <TableCell align="center">Completion Rate</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assessments.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body1" color="textSecondary">
                        No surveys found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  assessments.map((assessment) => {
                    const stats = statistics.get(assessment.id);
                    return (
                      <TableRow key={assessment.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AssessmentIcon color="primary" />
                            <Box>
                              <Typography variant="body1">{assessment.title}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                {assessment.estimatedMinutes} min
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{assessment.category}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={assessment.isActive ? 'Active' : 'Inactive'}
                            color={assessment.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">{assessment.totalQuestions || 0}</TableCell>
                        <TableCell align="center">
                          {stats ? (
                            <Box>
                              <Typography variant="body2">
                                {stats.completedAssignments}/{stats.totalAssignments}
                              </Typography>
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {stats && stats.totalAssignments > 0 ? (
                            <Box>
                              <Typography variant="body2">
                                {stats.completionRate.toFixed(0)}%
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={stats.completionRate}
                                sx={{ mt: 0.5, height: 4 }}
                              />
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell>{formatDate(assessment.createdAt)}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="View">
                              <IconButton
                                size="small"
                                onClick={() => handleViewAssessment(assessment)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditAssessment(assessment)}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Assign">
                              <IconButton
                                size="small"
                                onClick={() => handleAssignAssessment(assessment)}
                                color="primary"
                              >
                                <Assignment />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={assessment.isActive ? 'Deactivate' : 'Activate'}>
                              <IconButton
                                size="small"
                                onClick={() => handleToggleActive(assessment)}
                                color={assessment.isActive ? 'default' : 'success'}
                              >
                                {assessment.isActive ? <ToggleOff /> : <ToggleOn />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Duplicate">
                              <IconButton
                                size="small"
                                onClick={() => handleDuplicateAssessment(assessment)}
                              >
                                <ContentCopy />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteAssessment(assessment)}
                                color="error"
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage + 1}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </Card>
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the survey "{selectedAssessment?.title}"?
            This action cannot be undone and will affect all assignments.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteAssessment}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Survey Dialog */}
      {selectedAssessment && (
        <AssignSurveyDialog
          open={showAssignDialog}
          onClose={() => {
            setShowAssignDialog(false);
            setSelectedAssessment(null);
          }}
          assessment={selectedAssessment}
          onSuccess={() => {
            setShowAssignDialog(false);
            setSelectedAssessment(null);
            loadAssessments();
          }}
        />
      )}
    </>
  );
};

export default ManageSurveysPage;